import { ref } from 'vue';
import AgentsAPI from 'dashboard/api/agents';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';

const DEFAULT_COUNTS = Object.freeze({
  andamento: 0,
  revisoes: 0,
  finalizadas: 0,
  cancelados: 0,
});

const DEFAULT_DASHBOARD = Object.freeze({
  counts: DEFAULT_COUNTS,
  status_distribution: {},
  priority_distribution: {},
  responsible_queue: [],
  deadlines: { vermelhos: 0, laranjas: 0, amarelos: 0, sem_prazo: 0 },
});

export function usePostSaleOverview({
  filters,
  page,
  activeList,
  sort,
  t,
  notify,
}) {
  const loading = ref(false);
  const loadingDashboard = ref(false);
  const error = ref('');
  const cases = ref([]);
  const counts = ref({ ...DEFAULT_COUNTS });
  const dashboard = ref({ ...DEFAULT_DASHBOARD });
  const agents = ref([]);
  const clients = ref([]);
  const addresses = ref([]);
  const totalPages = ref(1);
  const totalRows = ref(0);
  const loadingClients = ref(false);
  const loadingAddresses = ref(false);

  const errorMessage = requestError =>
    requestError?.response?.data?.message ||
    requestError?.response?.data?.error ||
    t('POST_SALE.ALERTS.LOAD_ERROR');

  const filterParams = () => ({
    q: filters.value.q || undefined,
    responsible_user_id: filters.value.responsible_user_id || undefined,
    priority: filters.value.priority || undefined,
    deadline: filters.value.deadline || undefined,
    payment_method: filters.value.payment_method || undefined,
    laudemio_type: filters.value.laudemio_type || undefined,
    clients: filters.value.clients?.length ? filters.value.clients : undefined,
    address_street: filters.value.address_street || undefined,
    address_number: filters.value.address_number || undefined,
    address_unit: filters.value.address_unit || undefined,
    sort: sort?.value || undefined,
  });

  const loadCases = async () => {
    loading.value = true;
    error.value = '';

    try {
      const { data } = await PostSaleCasesAPI.list({
        ...filterParams(),
        list: activeList.value,
        page: page.value,
      });
      cases.value = data?.data || [];
      totalRows.value = data?.meta?.count || 0;
      totalPages.value = data?.meta?.total_pages || 1;
    } catch (requestError) {
      error.value = errorMessage(requestError);
    } finally {
      loading.value = false;
    }
  };

  const loadDashboard = async () => {
    loadingDashboard.value = true;

    try {
      const [{ data: dashboardData }, { data: countsData }] = await Promise.all(
        [PostSaleCasesAPI.dashboard(), PostSaleCasesAPI.counts()]
      );
      dashboard.value = { ...DEFAULT_DASHBOARD, ...(dashboardData || {}) };
      counts.value = {
        ...DEFAULT_COUNTS,
        ...(countsData || dashboardData?.counts || {}),
      };
    } catch (requestError) {
      notify(errorMessage(requestError));
    } finally {
      loadingDashboard.value = false;
    }
  };

  const loadAgents = async () => {
    try {
      const { data } = await AgentsAPI.get();
      agents.value = data?.payload || data || [];
    } catch (requestError) {
      agents.value = [];
    }
  };

  const loadClients = async (list = activeList.value) => {
    loadingClients.value = true;
    try {
      const { data } = await PostSaleCasesAPI.clients({ list });
      clients.value = data?.data || [];
    } catch {
      clients.value = [];
    } finally {
      loadingClients.value = false;
    }
  };

  const loadAddresses = async query => {
    if (!query?.trim()) {
      addresses.value = [];
      loadingAddresses.value = false;
      return;
    }

    loadingAddresses.value = true;
    try {
      const { data } = await PostSaleCasesAPI.addresses({
        list: activeList.value,
        q: query,
      });
      addresses.value = data?.data || [];
    } catch {
      addresses.value = [];
    } finally {
      loadingAddresses.value = false;
    }
  };

  const refresh = () =>
    Promise.all([loadCases(), loadDashboard(), loadClients()]);

  return {
    agents,
    addresses,
    cases,
    clients,
    counts,
    dashboard,
    error,
    loadAgents,
    loadCases,
    loadClients,
    loadAddresses,
    loadDashboard,
    loading,
    loadingDashboard,
    loadingClients,
    loadingAddresses,
    refresh,
    totalPages,
    totalRows,
  };
}
